import { CheckCircleIcon } from '@heroicons/react/24/solid'

import { usePermissions } from '@/api/models/users/hooks/usePermissions'
import { Permission } from '@/api/models/users/lib/users.types'
import { cn } from '@/app/utils'
import { useSelectedProjectStore } from '@/components/entities/common/model/selected-project.store'
import { ErrorCard, ErrorForbidden } from '@/components/pages/plugs'
import { AppDisclosure } from '@/components/shared/disclosure'
import { isForbiddenError } from '@/utils/isForbiddenError'

import { UserContactsListSkeleton } from './UserContactsSkeleton'

export const UserPermissionsScreen = () => {
  const { selectedProjectId: projectId } = useSelectedProjectStore()

  const {
    data: permissions = [],
    isLoading: arePermissionsLoading,
    isError: arePermissionsError,
    error: permissionsError
  } = usePermissions()

  if (isForbiddenError(permissionsError ?? null)) {
    return (
      <div className="mt-[32%] sm:mt-[55%]">
        <ErrorForbidden />
      </div>
    )
  }

  if (arePermissionsError) {
    return (
      <div className="mt-[32%] sm:mt-[55%]">
        <ErrorCard />
      </div>
    )
  }

  const checkProjectSelection = (permission: Permission) => {
    return projectId === permission.projectId
  }

  const selectedProject = permissions?.find(checkProjectSelection)

  const permissionsList = [
    selectedProject!,
    ...(permissions?.filter(permission => permission.projectId !== projectId) ??
      [])
  ]

  return (
    <div className="mt-12 flex flex-col gap-12 xxs:mt-8 xxs:gap-8">
      {arePermissionsLoading ? (
        <UserContactsListSkeleton />
      ) : (
        <>
          {permissionsList.map(permission => (
            <AppDisclosure
              key={permission.projectId}
              defaultOpened={checkProjectSelection(permission)}
              buttonRender={
                <div className="flex gap-4">
                  {checkProjectSelection(permission) && (
                    <CheckCircleIcon
                      className={cn(
                        'mr-4 h-20 w-20 self-center text-bronze-40',
                        'sm:mr-0 sm:h-16 sm:w-16'
                      )}
                    />
                  )}
                  {permission.projectName}
                </div>
              }
              buttonClassName="bg-white font-bold !p-16 !text-20 hover:bg-white sm:!text-16"
              panelClassName="mt-0"
              className="rounded-12 bg-white"
            >
              {permission.premises.map((premise, index) => (
                <div key={premise.premiseId}>
                  <div className="flex justify-between p-16">
                    <div className="flex w-[265px] flex-col justify-center gap-4 break-all xxs:w-[155px]">
                      <p className="text-16 xxs:text-14">
                        {premise.premiseName}
                      </p>
                      <p className="text-bronze-40 text-16 xxs:text-14">
                        {premise.permissionRules}
                      </p>
                    </div>
                    <div className="w-[265px] text-16 xxs:w-[155px] xxs:text-14">
                      {premise.permissionTypes.join(', ')}
                    </div>
                  </div>
                  {index !== permission.premises.length - 1 && (
                    <hr className="text-grey-20" />
                  )}
                </div>
              ))}
            </AppDisclosure>
          ))}
        </>
      )}
    </div>
  )
}
